import { FC, useEffect, useState } from "react";
import { Button, Col, Input, Row, message } from "antd";
import { ImportOutlined } from "@ant-design/icons";

import { RfxAnalysisOutput } from "../../../../types/ProjectTypes";
import { Tactic } from "../../../../types/TacticTypes";
import { convertGoogleDoc } from "../../../../common/api/ReportClient";
import { splitContentByH2 } from "../../../../common/api/TacticClient";
import { handleError } from "../../../../common/ErrorHandling";
import { TacticContent } from "../../../tactic/detail-v2/TacticContent";
import { GoogleShareHint } from "./GoogleShareHint";

interface RfxReportTabProps {
    output: RfxAnalysisOutput;
    onChange: (output: RfxAnalysisOutput) => void;
}

/**
 * Import tactic report from Google Doc and save it on RfxAnalysisOutput.
 */
export const RfxReportTab: FC<RfxReportTabProps> = ({ output, onChange }) => {
    const [googleDocUrl, setGoogleDocUrl] = useState("");
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        setGoogleDocUrl(output.sourceUrls?.report ?? "");
    }, [output]);

    const handleImport = async () => {
        if (!googleDocUrl.trim()) {
            message.warning("Please enter a Google Doc URL or File ID");
            return;
        }
        setIsImporting(true);
        try {
            const result = (await convertGoogleDoc(googleDocUrl.trim())) as {
                data: { sections: Array<{ id: number; title: string; content: string }> };
            };
            const sections = result.data?.sections;
            if (sections && sections.length > 0) {
                const content = sections.map((s) => s.content).join("");
                onChange({
                    ...output,
                    report: { content },
                    sourceUrls: { ...output.sourceUrls, report: googleDocUrl.trim() },
                });
                message.success("Google Doc imported");
            } else {
                message.error("No sections found in the Google Doc");
            }
        } catch (error) {
            handleError(error);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div>
            <Row gutter={8} style={{ marginBottom: 16 }}>
                <Col flex="auto">
                    <Input
                        placeholder="Enter Google Doc URL or File ID"
                        value={googleDocUrl}
                        onChange={(e) => setGoogleDocUrl(e.target.value)}
                        onPressEnter={handleImport}
                        disabled={isImporting}
                    />
                </Col>
                <Col>
                    <Button icon={<ImportOutlined />} onClick={handleImport} loading={isImporting}>
                        Import
                    </Button>
                </Col>
            </Row>
            <GoogleShareHint />

            {output.report?.content && (
                <div style={{ marginTop: 16 }}>
                    <TacticContent
                        tactic={splitContentByH2({
                            content: output.report.content,
                        } as Tactic)}
                    />
                </div>
            )}
        </div>
    );
};
